import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  learningMetricsService,
  type TrackSegmentPlayPayload,
  type GetTopReviewedParams,
  type GetVocabularyDensityParams,
} from '../services/learningMetrics.service';
import { getEngagementSummary } from '../services/youtube.service';

// Query key factory
export const learningMetricsKeys = {
  all: ['learning-metrics'] as const,
  topReviewed: (params?: GetTopReviewedParams) =>
    [...learningMetricsKeys.all, 'top-reviewed', params] as const,
  vocabularyDensity: (params?: GetVocabularyDensityParams) =>
    [...learningMetricsKeys.all, 'vocabulary-density', params] as const,
  youtubeSummary: () =>
    [...learningMetricsKeys.all, 'youtube-engagement-summary'] as const,
};

// Get top reviewed segments
export function useTopReviewedSegments(params?: GetTopReviewedParams) {
  return useQuery({
    queryKey: learningMetricsKeys.topReviewed(params),
    queryFn: () => learningMetricsService.getTopReviewedSegments(params),
    staleTime: 60000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// Get vocabulary density
export function useVocabularyDensity(params?: GetVocabularyDensityParams) {
  return useQuery({
    queryKey: learningMetricsKeys.vocabularyDensity(params),
    queryFn: () => learningMetricsService.getVocabularyDensity(params),
    staleTime: 60000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// Track segment play mutation
export function useTrackSegmentPlay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TrackSegmentPlayPayload) =>
      learningMetricsService.trackSegmentPlay(payload),
    onSuccess: () => {
      // Invalidate top reviewed segments query to refresh the data
      queryClient.invalidateQueries({
        queryKey: learningMetricsKeys.all,
      });
    },
  });
}

// Get YouTube engagement summary (Story 5.2)
export function useYouTubeEngagementSummary() {
  return useQuery({
    queryKey: learningMetricsKeys.youtubeSummary(),
    queryFn: () => getEngagementSummary(),
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
  });
}
